// pages/ApiDocs.jsx
import React, { useState, useEffect } from 'react';
import { FiCopy, FiCheck, FiKey } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth, api } from '../context/AuthContext';

function ApiDocs() {
  const { user, token } = useAuth();
  const [copied, setCopied] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);

  useEffect(() => {
    if (token) {
      fetchApiKeys();
    }
  }, [token]);

  const fetchApiKeys = async () => {
    try {
      const response = await api.get('/api/developer/api-keys');
      if (response.data.success) {
        setApiKeys(response.data.keys || []);
        // Select the first active key as default
        const activeKey = response.data.keys.find(k => k.active !== false);
        if (activeKey) {
          setSelectedKey(activeKey);
        }
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(null), 3000);
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return '#2ecc71';
      case 'POST': return '#3498db';
      case 'PUT': return '#ffa502';
      case 'DELETE': return '#ff4757';
      case 'PATCH': return '#a29bfe';
      default: return '#6b7a8f';
    }
  };

  const endpoints = [
    {
      method: 'POST',
      path: '/api/auth/login',
      description: 'Login to get authentication token',
      body: { username: 'string', password: 'string' },
      response: { success: true, token: 'string', user: 'object' },
    },
    {
      method: 'POST',
      path: '/api/auth/register',
      description: 'Register a new user',
      body: { username: 'string', email: 'string', password: 'string', role: 'string' },
      response: { success: true, user: 'object' },
    },
    {
      method: 'GET',
      path: '/api/users',
      description: 'Get all users (requires authentication)',
      headers: { Authorization: 'Bearer {token}' },
      response: { success: true, users: 'array' },
    },
    {
      method: 'GET',
      path: '/api/users/json',
      description: 'Get all users in JSON format (requires authentication)',
      headers: { Authorization: 'Bearer {token}' },
      response: { success: true, data: { users: 'array' } },
    },
    {
      method: 'GET',
      path: '/api/drivers',
      description: 'Get all drivers (requires authentication)',
      headers: { Authorization: 'Bearer {token}' },
      response: { success: true, drivers: 'array' },
    },
    {
      method: 'GET',
      path: '/api/rides',
      description: 'Get all rides (requires authentication)',
      headers: { Authorization: 'Bearer {token}' },
      response: { success: true, rides: 'array' },
    },
    {
      method: 'GET',
      path: '/api/share/price',
      description: 'Get current share price',
      response: { success: true, data: 'object' },
    },
    {
      method: 'POST',
      path: '/api/share/buy',
      description: 'Buy shares (requires authentication)',
      headers: { Authorization: 'Bearer {token}' },
      body: { shares: 'number' },
      response: { success: true, shares: 'number', price: 'number', totalCost: 'number' },
    },
    {
      method: 'POST',
      path: '/api/share/sell',
      description: 'Sell shares (requires authentication)',
      headers: { Authorization: 'Bearer {token}' },
      body: { shares: 'number' },
      response: { success: true, shares: 'number', price: 'number', totalValue: 'number' },
    },
  ];

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>📖 API Documentation</h2>

      {/* Authentication Card with Real API Key */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">🔐 Authentication</span>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {apiKeys.length} key{apiKeys.length !== 1 ? 's' : ''} available
          </span>
        </div>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
          All API requests (except login and register) require authentication. Use your API key in the request header:
        </p>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '12px' }}>
            <div className="spinner" style={{ width: '20px', height: '20px', margin: '0 auto' }}></div>
          </div>
        ) : apiKeys.length === 0 ? (
          <div style={{ 
            background: 'var(--bg-input)', 
            padding: '16px', 
            borderRadius: '8px',
            textAlign: 'center',
            border: '1px dashed var(--border-color)'
          }}>
            <p style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>
              <FiKey size={24} style={{ display: 'block', margin: '0 auto 8px' }} />
              No API keys found.
            </p>
            <button 
              className="btn btn-primary" 
              onClick={() => window.location.href = '/api-keys'}
              style={{ padding: '6px 16px', fontSize: '13px' }}
            >
              Generate API Key
            </button>
          </div>
        ) : (
          <div>
            {/* Key Selector */}
            {apiKeys.length > 1 && (
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '13px', color: 'var(--text-secondary)', marginRight: '8px' }}>
                  Select API Key:
                </label>
                <select
                  value={selectedKey?.id || ''}
                  onChange={(e) => {
                    const key = apiKeys.find(k => k.id === e.target.value);
                    setSelectedKey(key);
                  }}
                  style={{
                    background: 'var(--bg-input)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '13px',
                  }}
                >
                  {apiKeys.map(key => (
                    <option key={key.id} value={key.id}>
                      {key.name} {key.active !== false ? '✅' : '🔴'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Display Selected Key */}
            {selectedKey && (
              <div style={{ 
                background: 'var(--bg-input)', 
                padding: '12px 16px', 
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {selectedKey.name}
                      <span style={{ 
                        marginLeft: '8px',
                        padding: '1px 8px',
                        borderRadius: '10px',
                        fontSize: '10px',
                        background: selectedKey.active !== false ? '#0d3a2a' : '#3a1a1a',
                        color: selectedKey.active !== false ? '#2ecc71' : '#e74c3c',
                      }}>
                        {selectedKey.active !== false ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>
                    <code style={{ 
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                      color: 'var(--text-primary)',
                    }}>
                      {selectedKey.key}
                    </code>
                  </div>
                  <button 
                    className="copy-btn"
                    onClick={() => copyToClipboard(selectedKey.key, selectedKey.id)}
                    style={{
                      background: 'var(--border-color)',
                      border: 'none',
                      color: 'var(--text-primary)',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                    }}
                  >
                    {copied === selectedKey.id ? <FiCheck /> : <FiCopy />}
                    {copied === selectedKey.id ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div style={{ 
                  fontSize: '11px', 
                  color: 'var(--text-muted)', 
                  marginTop: '6px',
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}>
                  <span>Created: {new Date(selectedKey.createdAt).toLocaleString()}</span>
                  <span>Permissions: {selectedKey.permissions?.join(', ') || 'read'}</span>
                  <span>Last Used: {selectedKey.lastUsed ? new Date(selectedKey.lastUsed).toLocaleString() : 'Never'}</span>
                </div>
              </div>
            )}

            {/* Usage Examples */}
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Usage Examples:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ 
                  background: 'var(--bg-input)', 
                  padding: '6px 12px', 
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                }}>
                  <span style={{ color: 'var(--text-muted)' }}># Bearer Token</span>
                  <br />
                  Authorization: Bearer {selectedKey.key}
                </div>
                <div style={{ 
                  background: 'var(--bg-input)', 
                  padding: '6px 12px', 
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                }}>
                  <span style={{ color: 'var(--text-muted)' }}># API Key Header</span>
                  <br />
                  X-API-Key: {selectedKey.key}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Endpoints */}
      <div style={{ marginTop: '20px' }}>
        <h3 style={{ marginBottom: '12px', color: 'var(--text-secondary)' }}>📡 Endpoints</h3>
        {endpoints.map((endpoint, index) => (
          <div className="card" key={index}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <span style={{ 
                background: getMethodColor(endpoint.method), 
                color: 'white', 
                padding: '2px 12px', 
                borderRadius: '4px', 
                fontSize: '12px',
                fontWeight: '700',
              }}>
                {endpoint.method}
              </span>
              <code style={{ fontSize: '16px', fontWeight: '600' }}>{endpoint.path}</code>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>{endpoint.description}</p>
            
            {endpoint.headers && (
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Headers:</strong>
                <pre style={{ 
                  background: 'var(--bg-input)', 
                  padding: '8px 12px', 
                  borderRadius: '6px', 
                  marginTop: '4px',
                  fontSize: '13px',
                  overflow: 'auto',
                }}>
                  {JSON.stringify(endpoint.headers, null, 2)}
                </pre>
              </div>
            )}

            {endpoint.body && (
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Request Body:</strong>
                <pre style={{ 
                  background: 'var(--bg-input)', 
                  padding: '8px 12px', 
                  borderRadius: '6px', 
                  marginTop: '4px',
                  fontSize: '13px',
                  overflow: 'auto',
                }}>
                  {JSON.stringify(endpoint.body, null, 2)}
                </pre>
              </div>
            )}

            {endpoint.response && (
              <div>
                <strong style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Response:</strong>
                <pre style={{ 
                  background: 'var(--bg-input)', 
                  padding: '8px 12px', 
                  borderRadius: '6px', 
                  marginTop: '4px',
                  fontSize: '13px',
                  overflow: 'auto',
                }}>
                  {JSON.stringify(endpoint.response, null, 2)}
                </pre>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Error Codes */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">📦 Error Codes</span>
        </div>
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>200</td><td>Success</td></tr>
              <tr><td>201</td><td>Created</td></tr>
              <tr><td>400</td><td>Bad Request - Missing or invalid parameters</td></tr>
              <tr><td>401</td><td>Unauthorized - Invalid or missing authentication</td></tr>
              <tr><td>403</td><td>Forbidden - Insufficient permissions</td></tr>
              <tr><td>404</td><td>Not Found</td></tr>
              <tr><td>409</td><td>Conflict - Resource already exists</td></tr>
              <tr><td>500</td><td>Internal Server Error</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ApiDocs;